/**
 * offlineManager.js
 * Maneja el caché de datos y la cola de pedidos para uso sin conexión.
 * El caché general usa localStorage. La cola de pedidos usa IndexedDB como
 * única fuente de verdad (más resistente que localStorage a límites de
 * espacio y a la limpieza agresiva de Safari/iOS).
 */

const PREFIX = 'offl_'
const LEGACY_QUEUE_KEY = `${PREFIX}queue`
const DB_NAME = 'mobile_orders_offline_db'
const DB_VERSION = 1
const STORE_QUEUE = 'orders_queue'

let queueMemory = null
let dbPromise = null

function safeGet(key) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch (e) {
    console.warn('[OfflineManager] Error al guardar en localStorage:', key, e)
    return false
  }
}

function setQueueMemory(queue) {
  queueMemory = Array.isArray(queue) ? queue : []
  return queueMemory
}

function openQueueDb() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB no disponible'))
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = event => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        db.createObjectStore(STORE_QUEUE, { keyPath: 'localId' })
      }
    }

    request.onsuccess = event => resolve(event.target.result)
    request.onerror = () => reject(request.error || new Error('No se pudo abrir IndexedDB'))
  })

  return dbPromise
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error('Operación IndexedDB fallida'))
  })
}

async function loadQueueFromIndexedDb() {
  try {
    const db = await openQueueDb()
    const store = db.transaction(STORE_QUEUE, 'readonly').objectStore(STORE_QUEUE)
    const items = await requestToPromise(store.getAll())
    return (items || []).sort((a, b) => String(a.savedAt || '').localeCompare(String(b.savedAt || '')))
  } catch (e) {
    console.warn('[OfflineManager] Error al leer cola desde IndexedDB:', e)
    return []
  }
}

async function saveQueueItemToIndexedDb(item) {
  try {
    const db = await openQueueDb()
    const store = db.transaction(STORE_QUEUE, 'readwrite').objectStore(STORE_QUEUE)
    await requestToPromise(store.put(item))
    return true
  } catch (e) {
    console.warn('[OfflineManager] Error al guardar item de cola en IndexedDB:', e)
    return false
  }
}

async function deleteQueueItemFromIndexedDb(localId) {
  try {
    const db = await openQueueDb()
    const store = db.transaction(STORE_QUEUE, 'readwrite').objectStore(STORE_QUEUE)
    await requestToPromise(store.delete(localId))
    return true
  } catch (e) {
    console.warn('[OfflineManager] Error al borrar item de cola en IndexedDB:', e)
    return false
  }
}

// Migración única: hasta esta versión la cola vivía (también) en localStorage.
// Se rescata cualquier pedido que haya quedado ahí y no esté en IndexedDB,
// y se elimina la copia vieja para que esto corra una sola vez.
async function migrateLegacyLocalStorageQueue(indexedQueue) {
  const legacy = safeGet(LEGACY_QUEUE_KEY)
  if (!Array.isArray(legacy) || !legacy.length) {
    if (legacy !== null) localStorage.removeItem(LEGACY_QUEUE_KEY)
    return indexedQueue
  }

  const byId = new Map()
  indexedQueue.forEach(item => byId.set(item.localId, item))
  legacy.forEach(item => {
    if (!byId.has(item.localId)) byId.set(item.localId, item)
  })

  const merged = Array.from(byId.values()).sort((a, b) => String(a.savedAt || '').localeCompare(String(b.savedAt || '')))
  const results = await Promise.all(merged.map(item => saveQueueItemToIndexedDb(item)))

  // La copia legacy solo se elimina si TODOS los items quedaron a salvo en
  // IndexedDB: si el guardado falló (IndexedDB rota, sin espacio), la clave
  // queda en localStorage y la migración reintenta en el próximo arranque.
  if (results.every(Boolean)) {
    localStorage.removeItem(LEGACY_QUEUE_KEY)
  }

  return merged
}

// Si la app se cerró/crasheó a mitad de un envío, el item queda en 'syncing'
// para siempre y desaparece de los contadores. Se recupera como 'pending'.
function recoverStuckItems(queue) {
  let recovered = false
  const fixed = queue.map(item => {
    if (item.status !== 'syncing') return item
    recovered = true
    return {
      ...item,
      status: 'pending',
      error: item.error || 'Se recuperó un envío interrumpido',
    }
  })
  if (recovered) {
    fixed.filter(i => i.error === 'Se recuperó un envío interrumpido').forEach(i => saveQueueItemToIndexedDb(i))
  }
  return fixed
}

const OfflineManager = {

  isOnline() {
    return navigator.onLine
  },

  // ─────────────────────────────────────────────
  // Zonas + clientes del vendedor
  // ─────────────────────────────────────────────
  saveZones(sellerId, data) {
    return safeSet(`${PREFIX}zones_${sellerId}`, { data, ts: Date.now() })
  },
  getZones(sellerId) {
    const rec = safeGet(`${PREFIX}zones_${sellerId}`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Catálogo de productos
  // ─────────────────────────────────────────────
  saveProducts(sellerId, data) {
    return safeSet(`${PREFIX}prods_${sellerId}`, { data, ts: Date.now() })
  },
  getProducts(sellerId) {
    const rec = safeGet(`${PREFIX}prods_${sellerId}`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Precios por producto + cliente + lista
  // Se cachean automáticamente cuando se cargan online
  // ─────────────────────────────────────────────
  savePrice(productId, customerId, priceListId, priceData) {
    const key = `${PREFIX}price_${productId}_${customerId}_${priceListId}`
    return safeSet(key, { data: priceData, ts: Date.now() })
  },
  getPrice(productId, customerId, priceListId) {
    // 1. Intentar precio específico del cliente
    const key = `${PREFIX}price_${productId}_${customerId}_${priceListId}`
    const rec = safeGet(key)
    if (rec) return rec.data
    // 2. Fallback: precio base de la lista (sin acuerdo de cliente)
    const baseKey = `${PREFIX}price_${productId}_null_${priceListId}`
    const baseRec = safeGet(baseKey)
    return baseRec ? baseRec.data : null
  },

  // ─────────────────────────────────────────────
  // Listas de precios
  // ─────────────────────────────────────────────
  savePriceLists(data) {
    return safeSet(`${PREFIX}price_lists`, { data, ts: Date.now() })
  },
  getPriceLists() {
    const rec = safeGet(`${PREFIX}price_lists`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Unidades de medida
  // ─────────────────────────────────────────────
  saveUnitMeasurements(data) {
    return safeSet(`${PREFIX}unit_meas`, { data, ts: Date.now() })
  },
  getUnitMeasurements() {
    const rec = safeGet(`${PREFIX}unit_meas`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Puntos de venta
  // ─────────────────────────────────────────────
  savePointsSales(data) {
    return safeSet(`${PREFIX}points_sales`, { data, ts: Date.now() })
  },
  getPointsSales() {
    const rec = safeGet(`${PREFIX}points_sales`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Medios de pago
  // ─────────────────────────────────────────────
  saveMethodsPayments(data) {
    return safeSet(`${PREFIX}methods_pay`, { data, ts: Date.now() })
  },
  getMethodsPayments() {
    const rec = safeGet(`${PREFIX}methods_pay`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Condiciones de IVA (para calcular precio c/IVA sin conexión)
  // ─────────────────────────────────────────────
  saveIvaConditions(data) {
    return safeSet(`${PREFIX}iva_conditions`, { data, ts: Date.now() })
  },
  getIvaConditions() {
    const rec = safeGet(`${PREFIX}iva_conditions`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Configuración del módulo
  // ─────────────────────────────────────────────
  saveConfigurations(data) {
    return safeSet(`${PREFIX}configs`, { data, ts: Date.now() })
  },
  getConfigurations() {
    const rec = safeGet(`${PREFIX}configs`)
    return rec ? rec.data : null
  },

  // ─────────────────────────────────────────────
  // Timestamp del último sync
  // ─────────────────────────────────────────────
  setLastSync(sellerId) {
    return safeSet(`${PREFIX}last_sync_${sellerId}`, Date.now())
  },
  getLastSync(sellerId) {
    return safeGet(`${PREFIX}last_sync_${sellerId}`)
  },
  formatLastSync(sellerId) {
    const ts = this.getLastSync(sellerId)
    if (!ts) return null
    const d = new Date(ts)
    return d.toLocaleDateString('es-AR') + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
  },

  // ─────────────────────────────────────────────
  // Cola de pedidos offline (IndexedDB como fuente de verdad)
  // ─────────────────────────────────────────────
  getQueue() {
    return queueMemory !== null ? queueMemory : setQueueMemory([])
  },

  // Se llama al montar/activar la pantalla: carga la cola desde IndexedDB,
  // migra restos de la versión anterior (localStorage) y recupera items
  // que quedaron trabados en 'syncing' por un cierre abrupto de la app.
  async restoreQueueFromBackup() {
    let queue = await loadQueueFromIndexedDb()
    queue = await migrateLegacyLocalStorageQueue(queue)
    queue = recoverStuckItems(queue)

    setQueueMemory(queue)
    return queue
  },

  async addToQueue(orderData, isEdit = false, extras = {}) {
    const queue = this.getQueue()
    const localId = `${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
    const item = {
      localId,
      savedAt: new Date().toISOString(),
      status: 'pending',   // pending | syncing | error
      error: null,
      attempts: 0,
      isEdit,
      // resaved: el alta viene de corregir un item que ya había intentado
      // sincronizarse; si el servidor responde idempotency_hit, las
      // correcciones deben aplicarse como edición sobre el pedido existente.
      resaved: !!extras.resaved,
      data: orderData,
    }

    const saved = await saveQueueItemToIndexedDb(item)
    if (!saved) return null

    setQueueMemory(queue.concat([item]))
    return localId
  },

  updateQueueItem(localId, updates) {
    const queue = this.getQueue()
    const idx = queue.findIndex(i => i.localId === localId)
    if (idx !== -1) {
      queue[idx] = { ...queue[idx], ...updates }
      setQueueMemory(queue)
      saveQueueItemToIndexedDb(queue[idx])
    }
  },

  removeFromQueue(localId) {
    const queue = this.getQueue().filter(i => i.localId !== localId)
    setQueueMemory(queue)
    deleteQueueItemFromIndexedDb(localId)
  },

  // Un pedido "en curso" (syncing) también cuenta como pendiente: nunca debe
  // desaparecer del contador mientras no haya confirmación del servidor.
  pendingCount() {
    return this.getQueue().filter(i => i.status === 'pending' || i.status === 'error' || i.status === 'syncing').length
  },

  // ─────────────────────────────────────────────
  // Limpieza de datos cacheados (preserva la cola de pedidos)
  // ─────────────────────────────────────────────
  clearCachedData(sellerId) {
    const keysToDelete = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key || !key.startsWith(PREFIX)) continue
      keysToDelete.push(key)
    }
    keysToDelete.forEach(k => localStorage.removeItem(k))
  },
}

export default OfflineManager
